<template>
  <div class="news-page-wrapper">
    <NewsPageRoof :is-toolbar-active="true" class="container-roof" />
    <div id="containerNews" ref="containerNews" class="container-news">
      <NewsPageAside
        v-if="rubricsStore.allRubrics.length"
        class="container-news__aside"
      />

      <div
        v-if="newsFeedStore.items.length === 0 && newsFeedStore.loading"
        class="container-news__loader"
      >
        <LocalLoader />
      </div>
      <template v-else-if="newsFeedStore.items.length > 0">
        <NewsPageNewsFeed
          :chosen-card-list="newsFeedStore.chosenCardList"
          :chosen-detail-card-list="newsFeedStore.chosenDetailCardList"
          :is-all-page-card-chosen="isAllPageCardChosen"
          :is-all-cards-chosen="newsFeedStore.isAllCardsChosen"
          :chosen-rubric-ids="chosenRubricIds"
          :is-toolbar-active="true"
          :is-show-full-news="isShowFullNews"
          :parent-container-width="containerWidth"
          class="container-news__newsfeed"
          :class="{ nopointer: newsFeedStore.pointer }"
          :excluded-cards-ids="newsFeedStore.excludedCardsIds"
          :excluded-detail-card-list="newsFeedStore.excludedDetailCardList"
          @update:chosen-card-list="updateChosenCardList"
          @toggle-all-cards="toggleAllCards"
          @toggle-all-cards-global="toggleAllCardsGlobal"
          @select-all-cards="selectAllCards"
          @reset-card-list="newsFeedStore.resetChosenCardList"
        />

        <NewsPageBodyPost
          v-if="
            newsFeedStore.currentNews &&
            newsFeedStore.items?.length &&
            !newsFeedStore.loading
          "
          :key="newsFeedStore.currentNews.id"
          class="container-news__post"
          :class="{ nopointer: newsFeedStore.pointer }"
          @update:is-show-full-news="isShowFullNews = $event"
        />
        <div
          v-else-if="newsFeedStore.items.length > 0 && newsFeedStore.loading"
          class="container-news__post--loader"
        >
          <LocalLoader />
        </div>
      </template>
      <NewsPageBodyEmptyNews
        v-else-if="
          newsFeedStore.emptyNews || rubricsStore.chosenRubrics.length === 0
        "
      />
    </div>
  </div>
</template>

<script setup>
import NewsPageAside from "@/components/NewsPage/NewsPageAside.vue";
import NewsPageNewsFeed from "@/components/NewsPage/NewsPageNewsFeed.vue";
import NewsPageRoof from "@/components/NewsPage/NewsPageRoof/NewsPageRoof.vue";
import NewsPageBodyPost from "@/components/NewsPage/NewsPageBody/NewsPageBodyPost.vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import NewsPageBodyEmptyNews from "@/components/NewsPage/NewsPageBody/NewsPageBodyEmptyNews.vue";
import LocalLoader from "@/components/UI/LocalLoader.vue";
import { computed, ref, watch, onMounted, onUnmounted } from "vue";

const rubricsStore = useRubricsStore();
const newsFeedStore = useNewsFeedStore();

// Ref для контейнера новостей
const containerNews = ref(null);
const containerWidth = ref(0);

// Новость раскрыта на весь экран — тулбар ленты скрыт.
const isShowFullNews = ref(false);

const toChosenDetail = (card) => ({
  id: card.id,
  external_id: card.external_id,
  project_id: card.project_id,
});

// Идентификаторы выбранных рубрик из фильтра «Рубрика».
const chosenRubricIds = computed(() => rubricsStore.chosenRubrics);

const addPageCardsToChosen = () => {
  const excludedSet = new Set(newsFeedStore.excludedCardsIds);
  const knownIdSet = new Set(
    newsFeedStore.chosenDetailCardList.map((card) => card.id)
  );
  const pageCards = newsFeedStore.items.filter(
    (card) =>
      pageCardIdList.value.includes(card.id) && !excludedSet.has(card.id)
  );
  const pageDetails = pageCards
    .filter((card) => !knownIdSet.has(card.id))
    .map(toChosenDetail);

  // выборка для отображения чекбоксов
  newsFeedStore.chosenCardList = [
    ...new Set([
      ...newsFeedStore.chosenCardList,
      ...pageCards.map((card) => card.id),
    ]),
  ];

  // выборка для апи параметров
  newsFeedStore.chosenDetailCardList = [
    ...newsFeedStore.chosenDetailCardList,
    ...pageDetails,
  ];
};

const removePageCardsFromChosen = () => {
  const pageIdSet = new Set(pageCardIdList.value);

  // выборка для отображения чекбоксов
  newsFeedStore.chosenCardList = newsFeedStore.chosenCardList.filter(
    (id) => !pageIdSet.has(id)
  );

  // выборка для апи параметров
  newsFeedStore.chosenDetailCardList =
    newsFeedStore.chosenDetailCardList.filter(
      (card) => !pageIdSet.has(card.id)
    );
};

const detailsForExcludedIds = (idList) => {
  const idSet = new Set(idList);
  const knownIdSet = new Set(
    newsFeedStore.excludedDetailCardList.map((card) => card.id)
  );
  const details = [];

  for (const card of newsFeedStore.chosenDetailCardList) {
    if (!idSet.has(card.id) || knownIdSet.has(card.id)) continue;
    details.push(card);
    knownIdSet.add(card.id);
  }

  for (const card of newsFeedStore.items) {
    if (!idSet.has(card.id) || knownIdSet.has(card.id)) continue;
    details.push(toChosenDetail(card));
    knownIdSet.add(card.id);
  }

  return details;
};

const addExcludedCards = (idList) => {
  const details = detailsForExcludedIds(idList);
  newsFeedStore.excludedCardsIds = [
    ...new Set([...newsFeedStore.excludedCardsIds, ...idList]),
  ];
  newsFeedStore.excludedDetailCardList = [
    ...newsFeedStore.excludedDetailCardList,
    ...details,
  ];
};

const removeExcludedCards = (idList) => {
  const idSet = new Set(idList);
  newsFeedStore.excludedCardsIds = newsFeedStore.excludedCardsIds.filter(
    (id) => !idSet.has(id)
  );
  newsFeedStore.excludedDetailCardList =
    newsFeedStore.excludedDetailCardList.filter((card) => !idSet.has(card.id));
};

// Выбор всех карточек на всех страницах без снятия уже включённого режима.
const selectAllCards = () => {
  newsFeedStore.isAllCardsChosen = true;
  newsFeedStore.excludedCardsIds = [];
  newsFeedStore.excludedDetailCardList = [];
  addPageCardsToChosen();
};

// Выбор всех карточек на всех страницах
const toggleAllCardsGlobal = () => {
  newsFeedStore.isAllCardsChosen = !newsFeedStore.isAllCardsChosen;
  newsFeedStore.excludedCardsIds = [];
  newsFeedStore.excludedDetailCardList = [];

  if (newsFeedStore.isAllCardsChosen) {
    addPageCardsToChosen();
  } else {
    newsFeedStore.chosenCardList = [];
    newsFeedStore.chosenDetailCardList = [];
  }
};

const updateChosenCardList = (cardList) => {
  if (newsFeedStore.isAllCardsChosen) {
    const previousIdSet = new Set(newsFeedStore.chosenCardList);
    const nextIdSet = new Set(cardList);
    const uncheckedIds = newsFeedStore.chosenCardList.filter(
      (id) => !nextIdSet.has(id)
    );
    const checkedIds = cardList.filter((id) => !previousIdSet.has(id));

    addExcludedCards(uncheckedIds);
    removeExcludedCards(checkedIds);
  }

  const nextIdSet = new Set(cardList);
  const keptDetails = newsFeedStore.chosenDetailCardList.filter((card) =>
    nextIdSet.has(card.id)
  );
  const keptIdSet = new Set(keptDetails.map((card) => card.id));
  const addedDetails = newsFeedStore.items
    .filter((card) => nextIdSet.has(card.id) && !keptIdSet.has(card.id))
    .map(toChosenDetail);

  newsFeedStore.chosenCardList = cardList;
  newsFeedStore.chosenDetailCardList = [...keptDetails, ...addedDetails];
};

// Выбор всех карточек на странице.
// shouldSelect — состояние чекбокса после клика, а не пересчитанный список страницы.
const toggleAllCards = (shouldSelect) => {
  const select =
    typeof shouldSelect === "boolean"
      ? shouldSelect
      : !isAllPageCardChosen.value;

  if (!select) {
    removePageCardsFromChosen();
    if (newsFeedStore.isAllCardsChosen) addExcludedCards(pageCardIdList.value);
    return;
  }

  if (newsFeedStore.isAllCardsChosen) removeExcludedCards(pageCardIdList.value);
  addPageCardsToChosen();
};

// ID Всех карточек на странице
const pageCardIdList = computed(() =>
  newsFeedStore.items.map((item) => item.id)
);
// Выбраны ли все карточки на странице.
const isAllPageCardChosen = computed(() => {
  const pageIdList = pageCardIdList.value;
  const selectedIdList = newsFeedStore.chosenCardList;

  if (!pageIdList.length) return false;

  const selectedSet = new Set(selectedIdList);
  return pageIdList.every((id) => selectedSet.has(id));
});

// Карточки, подгруженные при переходе по страницам, сразу становятся выбранными.
watch(pageCardIdList, () => {
  if (newsFeedStore.isAllCardsChosen) addPageCardsToChosen();
});

// Обновление ширины контейнера
const updateContainerWidth = () => {
  if (containerNews.value) {
    containerWidth.value = containerNews.value.offsetWidth;
  }
};

onMounted(() => {
  updateContainerWidth();
  window.addEventListener("resize", updateContainerWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateContainerWidth);
});
</script>

<style lang="scss" scoped>
.news-page-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: calc(100vh - 80px);

  .container-news {
    display: grid;
    grid-template-columns: 231px minmax(450px, auto) minmax(471px, 1fr);
    overflow: hidden;
    gap: 14px;
    height: 100%;
    position: relative;

    &__loader {
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
    }

    &__header {
      grid-column: 1/4;
    }

    &__aside {
      grid-column: 1/2;
    }

    &__newsfeed {
      grid-column: 2/3;
      min-height: 0;
      overflow: hidden;

      &.nopointer {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }
    }

    &__post {
      grid-column: 3/4;

      &--loader {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
      }

      &.nopointer {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }
    }
  }
}
</style>
